import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { cloneDeep, memoize, mergeWith, sortBy, unionWith } from "lodash";
import { DateTime } from "luxon";
import type { DeepReadonly } from "ts-essentials";
import type { ModelField } from "../../database/ModelField";
import { FieldType } from "../../type-system/FieldType";
import type { ConnectionModelSpec } from "../utils";
import { ShopifyDominion } from "./ShopifyDominion";
import type { GraphQLListSyncConfig, GraphQLNodeListSyncConfig, GraphQLSubListSyncConfig, ShopifySyncConfig } from "./ShopifyModelMetadata";
import { satisfies } from "compare-versions";

/** Get the deprecation date for a particular Shopify API Version. Each is supported for 1 year and stable versions are released on the 1st of the month at 5pm UTC */
export const apiDeprecationDate = (apiVersion: ShopifyApiVersion) => {
  const [year, month] = apiVersion.split("-");

  return DateTime.fromObject(
    {
      year: parseInt(year),
      month: parseInt(month),
      day: 1,
      hour: 17,
    },
    { zone: "UTC" }
  ).plus({ year: 1 });
};

/** All currently available versions of the Shopify API */
export const SHOPIFY_API_VERSIONS = [
  "2022-01",
  "2022-04",
  "2022-07",
  "2022-10",

  "2023-01",
  "2023-04",
  "2023-07",
  "2023-10",

  "2024-01",
  "2024-04",
] as const;
export const LATEST_API_VERSION: ShopifyApiVersion = SHOPIFY_API_VERSIONS.slice(-1)[0];

export type ShopifyApiVersion = (typeof SHOPIFY_API_VERSIONS)[number];

/** Which version of the Shopify API is Gadget currently using for the code Gadget owns to talk to Shopify */
export const INTERNAL_SHOPIFY_API_VERSION: ShopifyApiVersion = "2024-04";

// Intermediate type that does not include selection because we build it when generating the spec
type ShopifySyncConfigDefinition =
  | Exclude<ShopifySyncConfig, GraphQLListSyncConfig | GraphQLSubListSyncConfig | GraphQLNodeListSyncConfig>
  | Omit<GraphQLListSyncConfig, "selection">
  | Omit<GraphQLSubListSyncConfig, "selection">
  | Omit<GraphQLNodeListSyncConfig, "selection">;

type ConnectionModelDefinition = { syncConfig: ShopifySyncConfigDefinition | null } & Omit<ConnectionModelSpec, "syncConfig" | "key">;

type ModelVersions = {
  [key in ShopifyApiVersion]: ConnectionModelDefinition;
};

type AtLeastOneModelVersion<T extends ModelVersions, U = { [K in keyof T]: Pick<T, K> }> = U[keyof U] &
  Partial<{
    [key in ShopifyApiVersion]: Partial<ConnectionModelDefinition>;
  }>;

export type ConnectionModelSpecVersion = { key: string } & AtLeastOneModelVersion<ModelVersions>;

const definedAndMatches = <T>(a: T, b: T) => {
  if (a === undefined || b === undefined) {
    return false;
  }

  return a === b;
};

export const buildConnectionSpec = memoize(
  (
    versions: ConnectionModelSpecVersion,
    untilVersion: ShopifyApiVersion,
    currentFrameworkVersionConstraint?: string
  ): ConnectionModelSpec | null => {
    const sorted = sortBy(
      Object.entries(versions).filter(([apiVersion]) => apiVersion <= untilVersion),
      ([apiVersion]) => apiVersion
    );

    const [first, ...rest] = sorted;
    let result: ConnectionModelDefinition;

    if (!first) {
      return null;
    }

    if (rest.length == 0) {
      result = cloneDeep(first[1]) as ConnectionModelDefinition;
    } else {
      result = mergeWith({}, first[1], ...rest.map((spec) => spec[1]), function checkKeys(targetValue: any, sourceValue: any) {
        if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
          return unionWith(sourceValue, targetValue, (a, b) => {
            const matches =
              definedAndMatches(a.key, b.key) ||
              definedAndMatches(
                a.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey,
                b.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey
              ) ||
              definedAndMatches(
                a.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey,
                b.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey
              ) ||
              definedAndMatches(a.apiIdentifer, b.apiIdentifer) ||
              definedAndMatches(a.name, b.name);

            return matches;
          }).filter((item) => ("isDeleted" in item ? !item.isDeleted : true));
        }
      });
    }

    if (result.fields) {
      (result as any).fields = result.fields.filter((item) => {
        if (!("frameworkVersionConstraint" in item)) return true;
        // if an item has a frameworkVersionConstraint and a currentFrameworkVersionConstraint isn't provided we will not include it
        if (!currentFrameworkVersionConstraint) return false;

        const constraint = item.frameworkVersionConstraint as string;
        return satisfies(currentFrameworkVersionConstraint, constraint);
      });
    }

    let connectionSpec: ConnectionModelSpec;
    if (result.fields && result.syncConfig && ["graphql_list", "graphql_sublist", "graphql_node_list"].includes(result.syncConfig.type)) {
      connectionSpec = {
        ...result,
        key: versions.key,
        syncConfig: {
          ...result.syncConfig,
          selection: (enabledModelKeys?: string[]) => buildSelectionFromFields(result.fields, enabledModelKeys ?? []),
        } as ShopifySyncConfig,
      };
    } else {
      connectionSpec = {
        ...result,
        key: versions.key,
      } as ConnectionModelSpec;
    }

    return connectionSpec;
  },
  (versions, untilVersion, currentFrameworkVersionConstraint) =>
    `${versions.key}//${untilVersion}//${currentFrameworkVersionConstraint ?? "no-framework-version"}`
);

export const buildSelectionFromFields = (
  fields: DeepReadonly<(SnapshotIn<typeof ModelField> & { isDeleted?: boolean })[]>,
  enabledModelKeys: string[],
  additionalSelection?: string
) => {
  let gql = `{\n`;
  const fragments: Record<string, string[]> = {};

  for (const field of fields) {
    // if we have a HasMany or HasManyThrough field then do not try to generate a selection for it as these are handled as subresources syncs that have their own graphql queries
    if (field.fieldType === FieldType.HasMany || field.fieldType === FieldType.HasManyThrough) {
      if (
        !(
          field.contributorConfiguration?.[ShopifyDominion.id]?.selectForHasMany &&
          enabledModelKeys.includes(field.configuration?.relatedModelKey)
        )
      ) {
        continue;
      }
    }

    if (field.fieldType === FieldType.HasOne || field.fieldType === FieldType.BelongsTo) {
      if (!enabledModelKeys.includes(field.configuration?.relatedModelKey)) {
        continue;
      }
    }

    if (field.contributorConfiguration && field.contributorConfiguration[ShopifyDominion.id]) {
      const graphQLFieldKey = field.contributorConfiguration[ShopifyDominion.id]?.graphQLFieldKey;
      const graphQLFragments = field.contributorConfiguration[ShopifyDominion.id]?.graphQLFragments;
      const graphQLSelection = field.contributorConfiguration[ShopifyDominion.id]?.graphQLSelection;

      if (!graphQLFieldKey || !!field.contributorConfiguration[ShopifyDominion.id]?.skipGraphQLSelection) {
        continue;
      }

      const fieldQuery = `${graphQLFieldKey}${graphQLSelection ? ` {\n${graphQLSelection}\n}` : ""}`;

      if (graphQLFragments) {
        graphQLFragments.forEach((fragment) => {
          if (fragments[fragment]) {
            fragments[fragment].push(fieldQuery);
          } else {
            fragments[fragment] = [fieldQuery];
          }
        });
      } else {
        gql += fieldQuery;
      }

      gql += "\n";
    }
  }

  for (const [key, graphQLFields] of Object.entries(fragments)) {
    gql += `... on ${key} {
      ${graphQLFields.join("\n")}
    }`;
  }

  if (additionalSelection) {
    gql += additionalSelection;
  }

  gql += "}";

  return gql;
};
